import type { LanguageCode } from "../i18n/languages";
import type { LegalDocument, LegalSlug } from "./types";

const loaders = import.meta.glob<{ default: LegalDocument }>(
  "../../content/legal/*/*.json",
  { eager: true },
);

/** slug → locale → document */
const bySlugAndLocale = new Map<string, Map<string, LegalDocument>>();

for (const [path, mod] of Object.entries(loaders)) {
  const doc = mod.default;
  // path like "../../content/legal/en/privacy.json"
  const match = path.match(/content\/legal\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const locale = match[1];
  const map = bySlugAndLocale.get(doc.slug) ?? new Map<string, LegalDocument>();
  map.set(locale, doc);
  bySlugAndLocale.set(doc.slug, map);
}

/** All published legal pages (English canonical text from AlbaVox v1.x documents). */
export const LEGAL_SLUGS = [
  "privacy",
  "terms",
  "cookies",
  "community-guidelines",
  "refunds",
  "supplier-agreement",
  "creator-monetization",
] as const satisfies readonly LegalSlug[];

export function getLegalDocument(
  slug: string,
  locale: LanguageCode | string = "en",
): LegalDocument | undefined {
  const map = bySlugAndLocale.get(slug);
  if (!map) return undefined;
  return map.get(locale) ?? map.get("en");
}

/** True when a non-English locale has its own translated file (not just EN fallback). */
export function hasLocalizedLegalDocument(
  slug: string,
  locale: LanguageCode | string,
): boolean {
  if (locale === "en") return true;
  return bySlugAndLocale.get(slug)?.has(locale) ?? false;
}

export function isLegalSlug(slug: string): slug is LegalSlug {
  return (LEGAL_SLUGS as readonly string[]).includes(slug);
}

/** Primary legal pages linked from the site footer. */
export const FOOTER_LEGAL_SLUGS: LegalSlug[] = [
  "privacy",
  "terms",
  "cookies",
  "community-guidelines",
  "refunds",
];
